import React, { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';

// Types for timer settings
interface PomodoroSettings {
  workDuration: number; // in minutes
  breakDuration: number; // in minutes
  longBreakDuration: number; // in minutes
  longBreakInterval: number; // after how many work sessions
  autoStartBreaks: boolean;
  autoStartWork: boolean;
}

// Type for tracking completed sessions
interface PomodoroHistory {
  date: string; // YYYY-MM-DD
  completedSessions: number;
}

interface PomodoroWidgetProps {
  title?: string;
  workDuration?: number;
  breakDuration?: number;
  longBreakDuration?: number;
  longBreakInterval?: number;
  autoStartBreaks?: boolean;
  autoStartWork?: boolean;
}

// Default settings
const DEFAULT_SETTINGS: PomodoroSettings = {
  workDuration: 25,
  breakDuration: 5,
  longBreakDuration: 15,
  longBreakInterval: 4,
  autoStartBreaks: true,
  autoStartWork: true,
};

// Local storage keys
const SETTINGS_KEY = 'pomodoro_settings';
const HISTORY_KEY = 'pomodoro_history';
const CURRENT_STATE_KEY = 'pomodoro_current_state';

type TimerMode = 'work' | 'break' | 'longBreak';

interface CurrentState {
  mode: TimerMode;
  timeRemaining: number; // in seconds
  isRunning: boolean;
  completedSessions: number;
  currentSession: number;
}

const PomodoroWidget: React.FC<PomodoroWidgetProps> = ({
  title = 'Pomodoro Timer',
  workDuration,
  breakDuration,
  longBreakDuration,
  longBreakInterval,
  autoStartBreaks,
  autoStartWork,
}) => {
  // State for settings
  const [settings, setSettings] = useState<PomodoroSettings>(() => {
    // Load settings from localStorage or use props or defaults
    const savedSettings = localStorage.getItem(SETTINGS_KEY);
    if (savedSettings) {
      return JSON.parse(savedSettings);
    }
    
    return {
      workDuration: workDuration || DEFAULT_SETTINGS.workDuration,
      breakDuration: breakDuration || DEFAULT_SETTINGS.breakDuration,
      longBreakDuration: longBreakDuration || DEFAULT_SETTINGS.longBreakDuration,
      longBreakInterval: longBreakInterval || DEFAULT_SETTINGS.longBreakInterval,
      autoStartBreaks: autoStartBreaks !== undefined ? autoStartBreaks : DEFAULT_SETTINGS.autoStartBreaks,
      autoStartWork: autoStartWork !== undefined ? autoStartWork : DEFAULT_SETTINGS.autoStartWork,
    };
  });

  // State for timer
  const [currentState, setCurrentState] = useState<CurrentState>(() => {
    // Try to load current state from localStorage
    const savedState = localStorage.getItem(CURRENT_STATE_KEY);
    if (savedState) {
      return JSON.parse(savedState);
    }
    
    // Default initial state
    return {
      mode: 'work' as TimerMode,
      timeRemaining: (workDuration || DEFAULT_SETTINGS.workDuration) * 60,
      isRunning: false,
      completedSessions: 0,
      currentSession: 1,
    };
  });

  // State for history data
  const [history, setHistory] = useState<PomodoroHistory[]>(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY);
    return savedHistory ? JSON.parse(savedHistory) : [];
  });
  
  // Timer interval reference
  const timerRef = useRef<number | null>(null);
  
  // Get today's date in YYYY-MM-DD format
  const getTodayDate = (): string => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };
  
  // Format time for display (MM:SS)
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Save current state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(CURRENT_STATE_KEY, JSON.stringify(currentState));
  }, [currentState]);
  
  // Save settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  }, [settings]);
  
  // Save history to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
  }, [history]);
  
  // Timer logic
  useEffect(() => {
    if (currentState.isRunning) {
      timerRef.current = window.setInterval(() => {
        setCurrentState(prevState => {
          if (prevState.timeRemaining <= 1) {
            // Timer finished
            clearInterval(timerRef.current!);
            
            let newMode: TimerMode = prevState.mode;
            let newTimeRemaining = 0;
            let newCompletedSessions = prevState.completedSessions;
            let newCurrentSession = prevState.currentSession;
            let shouldAutoStart = false;
            
            if (prevState.mode === 'work') {
              // Work session completed
              newCompletedSessions = prevState.completedSessions + 1;
              
              // Update history
              const today = getTodayDate();
              updateHistory(today);
              
              // Check if it's time for a long break
              if (prevState.currentSession % settings.longBreakInterval === 0) {
                newMode = 'longBreak';
                newTimeRemaining = settings.longBreakDuration * 60;
              } else {
                newMode = 'break';
                newTimeRemaining = settings.breakDuration * 60;
              }
              
              shouldAutoStart = settings.autoStartBreaks;
            } else {
              // Break completed, start next work session
              newMode = 'work';
              newTimeRemaining = settings.workDuration * 60;
              newCurrentSession = prevState.mode === 'longBreak' ? 1 : prevState.currentSession + 1;
              shouldAutoStart = settings.autoStartWork;
            }
            
            // Play notification sound
            const audio = new Audio('/notification.mp3');
            audio.play().catch(e => console.log('Audio play failed:', e));
            
            // Show browser notification if permitted
            if (Notification.permission === 'granted') {
              new Notification(
                prevState.mode === 'work' 
                  ? 'Time for a break!' 
                  : 'Break is over, back to work!'
              );
            }
            
            return {
              ...prevState,
              mode: newMode,
              timeRemaining: newTimeRemaining,
              isRunning: shouldAutoStart,
              completedSessions: newCompletedSessions,
              currentSession: newCurrentSession,
            };
          }
          
          return {
            ...prevState,
            timeRemaining: prevState.timeRemaining - 1
          };
        });
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [currentState.isRunning, settings]);
  
  // Update history with completed session
  const updateHistory = (date: string) => {
    setHistory(prevHistory => {
      // Check if we already have an entry for today
      const existingEntryIndex = prevHistory.findIndex(entry => entry.date === date);
      
      if (existingEntryIndex >= 0) {
        // Update existing entry
        const newHistory = [...prevHistory];
        newHistory[existingEntryIndex] = {
          ...newHistory[existingEntryIndex],
          completedSessions: newHistory[existingEntryIndex].completedSessions + 1
        };
        return newHistory;
      } else {
        // Create new entry for today
        return [...prevHistory, { date, completedSessions: 1 }];
      }
    });
  };
  
  // Start/pause timer
  const toggleTimer = () => {
    setCurrentState(prevState => ({
      ...prevState,
      isRunning: !prevState.isRunning
    }));
  };
  
  // Reset timer
  const resetTimer = () => {
    const newTimeRemaining = currentState.mode === 'work' 
      ? settings.workDuration * 60 
      : currentState.mode === 'break' 
        ? settings.breakDuration * 60 
        : settings.longBreakDuration * 60;
    
    setCurrentState(prevState => ({
      ...prevState,
      timeRemaining: newTimeRemaining,
      isRunning: false
    }));
  };
  
  // Skip to next timer
  const skipToNext = () => {
    let newMode: TimerMode;
    let newTimeRemaining: number;
    let newCurrentSession = currentState.currentSession;
    
    if (currentState.mode === 'work') {
      // Skip to break
      if (currentState.currentSession % settings.longBreakInterval === 0) {
        newMode = 'longBreak';
        newTimeRemaining = settings.longBreakDuration * 60;
      } else {
        newMode = 'break';
        newTimeRemaining = settings.breakDuration * 60;
      }
    } else {
      // Skip to work
      newMode = 'work';
      newTimeRemaining = settings.workDuration * 60;
      newCurrentSession = currentState.mode === 'longBreak' ? 1 : currentState.currentSession + 1;
    }
    
    setCurrentState(prevState => ({
      ...prevState,
      mode: newMode,
      timeRemaining: newTimeRemaining,
      isRunning: false,
      currentSession: newCurrentSession
    }));
  };
  
  // Get today's completed sessions
  const getTodayCompletedSessions = (): number => {
    const today = getTodayDate();
    const todayEntry = history.find(entry => entry.date === today);
    return todayEntry ? todayEntry.completedSessions : 0;
  };
  
  // Get day name from date string (YYYY-MM-DD)
  const getDayName = (dateString: string): string => {
    const date = new Date(dateString);
    return ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'][date.getDay()];
  };
  
  // Get the last 7 days of history
  const getWeeklyData = (): { date: string; day: string; count: number }[] => {
    const result = [];
    const today = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dateString = date.toISOString().split('T')[0];
      const dayName = getDayName(dateString);
      
      const historyEntry = history.find(entry => entry.date === dateString);
      const count = historyEntry ? historyEntry.completedSessions : 0;
      
      result.push({ date: dateString, day: dayName, count });
    }
    
    return result;
  };
  
  // Get the maximum count from the weekly data for scaling
  const getMaxWeeklyCount = (): number => {
    const weeklyData = getWeeklyData();
    const max = Math.max(...weeklyData.map(d => d.count));
    return max > 0 ? max : 1; // Avoid division by zero
  };
  
  // Request notification permission
  const requestNotificationPermission = () => {
    if (Notification.permission !== 'granted') {
      Notification.requestPermission();
    }
  };
  
  // Request notification permission on component mount
  useEffect(() => {
    requestNotificationPermission();
  }, []);
  
  // Get color based on current mode
  const getModeColor = (): string => {
    switch (currentState.mode) {
      case 'work':
        return 'text-red-500';
      case 'break':
        return 'text-green-500';
      case 'longBreak':
        return 'text-blue-500';
      default:
        return '';
    }
  };
  
  // Get mode display text
  const getModeText = (): string => {
    switch (currentState.mode) {
      case 'work':
        return 'Focus';
      case 'break':
        return 'Korte Pauze';
      case 'longBreak':
        return 'Lange Pauze';
      default:
        return '';
    }
  };
  
  // Toggle settings panel
  const [showSettings, setShowSettings] = useState(false);
  
  // Handle settings change
  const handleSettingChange = (setting: keyof PomodoroSettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 py-4">
        <CardTitle>{title}</CardTitle>
        <Button 
          variant="ghost" 
          size="sm"
          className="text-theme hover:bg-primary-theme-10 h-8 w-8 p-0 rounded-full"
          onClick={() => setShowSettings(!showSettings)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </Button>
      </CardHeader>
      <CardContent>
        {showSettings ? (
          <div className="space-y-4">
            <h3 className="text-theme font-medium mb-2">Instellingen</h3>
            
            <div>
              <label className="block text-theme-80 mb-1 text-sm">Focus tijd (minuten)</label>
              <input
                type="number"
                className="w-full bg-primary-theme-10 border border-theme-10 rounded px-3 py-2 text-theme"
                value={settings.workDuration}
                onChange={(e) => handleSettingChange('workDuration', parseInt(e.target.value) || DEFAULT_SETTINGS.workDuration)}
                min="1"
                max="60"
              />
            </div>
            
            <div>
              <label className="block text-theme-80 mb-1 text-sm">Korte pauze (minuten)</label>
              <input
                type="number"
                className="w-full bg-primary-theme-10 border border-theme-10 rounded px-3 py-2 text-theme"
                value={settings.breakDuration}
                onChange={(e) => handleSettingChange('breakDuration', parseInt(e.target.value) || DEFAULT_SETTINGS.breakDuration)}
                min="1"
                max="30"
              />
            </div>
            
            <div>
              <label className="block text-theme-80 mb-1 text-sm">Lange pauze (minuten)</label>
              <input
                type="number"
                className="w-full bg-primary-theme-10 border border-theme-10 rounded px-3 py-2 text-theme"
                value={settings.longBreakDuration}
                onChange={(e) => handleSettingChange('longBreakDuration', parseInt(e.target.value) || DEFAULT_SETTINGS.longBreakDuration)}
                min="1"
                max="60"
              />
            </div>
            
            <div>
              <label className="block text-theme-80 mb-1 text-sm">Lange pauze na (sessies)</label>
              <input
                type="number"
                className="w-full bg-primary-theme-10 border border-theme-10 rounded px-3 py-2 text-theme"
                value={settings.longBreakInterval}
                onChange={(e) => handleSettingChange('longBreakInterval', parseInt(e.target.value) || DEFAULT_SETTINGS.longBreakInterval)}
                min="1"
                max="10"
              />
            </div>
            
            <div className="flex items-center mt-4">
              <input
                type="checkbox"
                id="autoStartBreaks"
                checked={settings.autoStartBreaks}
                onChange={(e) => handleSettingChange('autoStartBreaks', e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="autoStartBreaks" className="text-theme-80 text-sm">
                Automatisch pauzes starten
              </label>
            </div>
            
            <div className="flex items-center mt-2">
              <input
                type="checkbox"
                id="autoStartWork"
                checked={settings.autoStartWork}
                onChange={(e) => handleSettingChange('autoStartWork', e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="autoStartWork" className="text-theme-80 text-sm">
                Automatisch focus sessies starten
              </label>
            </div>
            
            <div className="flex justify-end mt-4">
              <Button 
                variant="default"
                className="bg-secondary-theme hover:bg-secondary-theme/80 text-black"
                onClick={() => setShowSettings(false)}
              >
                Opslaan
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            {/* Timer display */}
            <div className="text-5xl font-bold mb-4">
              <span className={`${getModeColor()} text-shadow-lg`}>{formatTime(currentState.timeRemaining)}</span>
            </div>
            
            {/* Mode display */}
            <div className="text-lg mb-4">
              <span className={`${getModeColor()} text-shadow`}>{getModeText()}</span>
              {currentState.mode === 'work' && (
                <span className="text-sm ml-2 text-shadow">{currentState.currentSession}/{settings.longBreakInterval})</span>
              )}
            </div>
            
            {/* Timer controls */}
            <div className="flex gap-2 mb-6">
              <Button 
                onClick={toggleTimer}
                variant="default"
                className="bg-secondary-theme hover:bg-secondary-theme/80 text-black"
              >
                {currentState.isRunning ? 'Pauze' : 'Start'}
              </Button>
              <Button onClick={resetTimer} variant="outline" className="text-theme border-theme-20">
                Reset
              </Button>
              <Button onClick={skipToNext} variant="outline" className="text-theme border-theme-20">
                Skip
              </Button>
            </div>
            
            {/* Today's stats */}
            <div className="text-theme-80 text-sm mt-2">
              <p>Vandaag voltooid: <span className="font-medium text-theme">{getTodayCompletedSessions()}</span> sessies</p>
              <p className="mt-1">Totaal voltooid: <span className="font-medium text-theme">{currentState.completedSessions}</span> sessies</p>
            </div>
            
            {/* Weekly progress visualization */}
            <div className="mt-6 w-full">
              <h4 className="text-sm font-medium text-theme mb-2">Wekelijkse voortgang</h4>
              <div className="flex justify-between items-end h-24">
                {getWeeklyData().map((day) => {
                  const maxHeight = 80; // max height of the bar in pixels
                  const maxCount = getMaxWeeklyCount();
                  const height = day.count > 0 ? Math.max(20, (day.count / maxCount) * maxHeight) : 4;
                  
                  return (
                    <div key={day.date} className="flex flex-col items-center">
                      <div 
                        className={`w-6 rounded-t ${
                          day.date === getTodayDate() 
                            ? 'bg-secondary-theme' 
                            : day.count > 0 
                              ? 'bg-primary-theme-20' 
                              : 'bg-primary-theme-10'
                        }`}
                        style={{ height: `${height}px` }}
                      ></div>
                      <div className="text-xs text-theme-80 mt-1">{day.day}</div>
                      {day.count > 0 && (
                        <div className="text-xs font-medium text-theme">{day.count}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PomodoroWidget; 